import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Calendar, Eye, Bookmark, Share2, Facebook, Twitter, Send, Instagram, Pencil, Save, ImagePlus, Move, ZoomIn } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import RichContent from "@/components/RichContent";
import useSeo from "@/hooks/useSeo";
import MediaPicker from "@/components/admin/MediaPicker";
import { shareStoryImage } from "@/lib/share";
import StoryPreviewButton from "@/components/StoryPreviewButton";

export default function NewsDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [n, setN] = useState(null);
  const [related, setRelated] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);   // in-progress edits
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const dragRef = useRef(null);
  const canEdit = user && ["super_admin", "province_admin", "editor"].includes(user?.role);

  useEffect(() => {
    api.get(`/news/by-slug/${slug}`).then((r) => {
      setN(r.data);
      if (r.data.category_id) {
        api.get(`/news?category_id=${r.data.category_id}&limit=6`).then((rr) => setRelated((rr.data.items || []).filter((it) => it.id !== r.data.id)));
      }
    }).catch(() => {});
  }, [slug]);

  // Inject SEO meta tags for the current article. When `n` is null the hook
  // does nothing; once loaded it sets title/description/canonical/JSON-LD.
  const canonicalOrigin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo(n ? {
    title: (n.seo_title || n.title) + " | SİNE81",
    description: (n.seo_description || n.spot || (n.content || "").replace(/<[^>]+>/g, "")).slice(0, 300),
    keywords: n.seo_keywords || (n.tags || []).map((t) => t.name || t).join(", "),
    canonical: n.seo_canonical || `${canonicalOrigin}/haber/${n.slug}`,
    image: n.share_image || n.cover_image,
    noindex: !!n.seo_noindex,
    type: "article",
    publishedAt: n.publish_at || n.created_at,
    modifiedAt: n.updated_at || n.publish_at || n.created_at,
    author: n.author?.name || n.author_name || "SİNE81",
    section: n.category?.name,
    origin: canonicalOrigin,
  } : null);

  if (!n) return <div className="max-w-3xl mx-auto py-10 px-4"><div className="h-72 bg-gray-100 animate-pulse rounded-lg"></div></div>;

  const save = async () => {
    if (!user) return toast.error("Önce giriş yapmalısınız");
    try { await api.post("/saved-items", { entity: "news", entity_id: n.id }); toast.success("Kaydedildi"); } catch { toast.error("Kaydedilemedi"); }
  };
  // Use the OG endpoint as the shared URL so social media bots see rich previews.
  // Humans following the link get a 302 redirect to /haber/{slug} (the real React page).
  // Always use REACT_APP_BACKEND_URL because in production the backend may live on a
  // different subdomain than the frontend (e.g. api.sine81.com vs sine81.com).
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const shareUrl = `${backendUrl}/api/og/news/${n.slug}`;
  const storyUrl = `${backendUrl}/api/story/news/${n.slug}`;
  const shareTitle = encodeURIComponent(n.title);

  // Instagram does not support direct URL sharing on the web. We offer the best
  // experience available per platform:
  //   • Mobile (Web Share API) → open the OS share sheet so the user picks IG.
  //   • Desktop / no native share → copy the link and open instagram.com so
  //     the user can paste it into a story/DM/bio.
  const shareInstagram = async () => {
    const fullUrl = `${window.location.origin}/haber/${n.slug}`;
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    if (isMobile && navigator.share) {
      try {
        await navigator.share({ title: n.title, text: n.spot || n.title, url: fullUrl });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // user cancelled
      }
    }
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link kopyalandı. Instagram açılıyor — hikayenize/DM'inize yapıştırabilirsiniz.", { duration: 5000 });
    } catch {
      toast.message("Link kopyalanamadı. Linki manuel kopyalayıp Instagram'da paylaşabilirsiniz.", { duration: 5000 });
    }
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };

  const enterEdit = () => {
    // Backwards compat: if scale_x/y are not stored yet, seed them from
    // the old single `cover_scale` so the sliders start from the current
    // visual state.
    const baseScale = typeof n.cover_scale === "number" ? n.cover_scale : 1;
    // Convert any ISO datetime stored on the news record into the
    // "YYYY-MM-DDTHH:mm" shape <input type="datetime-local"> expects.
    const toLocalInput = (iso) => {
      if (!iso) return "";
      try {
        const d = new Date(iso);
        const pad = (x) => String(x).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch { return ""; }
    };
    setDraft({
      title: n.title || "",
      spot: n.spot || "",
      content: n.content || "",
      cover_image: n.cover_image || "",
      cover_focus_x: typeof n.cover_focus_x === "number" ? n.cover_focus_x : 50,
      cover_focus_y: typeof n.cover_focus_y === "number" ? n.cover_focus_y : 50,
      cover_scale: baseScale,
      cover_scale_x: typeof n.cover_scale_x === "number" ? n.cover_scale_x : baseScale,
      cover_scale_y: typeof n.cover_scale_y === "number" ? n.cover_scale_y : baseScale,
      source: n.source || "",
      source_url: n.source_url || "",
      publish_at: toLocalInput(n.publish_at),
      expire_at: toLocalInput(n.expire_at),
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // Strip client-only fields before sending, and convert the local
      // datetime inputs back into ISO strings for the backend.
      const { cover_link_axes, ...rest } = draft;
      const toIso = (v) => (v ? new Date(v).toISOString() : null);
      const payload = {
        ...rest,
        publish_at: toIso(draft.publish_at),
        expire_at: toIso(draft.expire_at),
      };
      const r = await api.put(`/news/${n.id}`, payload);
      setN(r.data);
      setEditMode(false);
      setDraft(null);
      toast.success("Değişiklikler kaydedildi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  const cancelEdit = () => { setEditMode(false); setDraft(null); };

  // Drag repositioning for the cover image (Facebook-style focus point)
  const onCoverDown = (e) => {
    if (!editMode) return;
    e.preventDefault();
    const box = e.currentTarget.getBoundingClientRect();
    const startX = e.clientX ?? e.touches?.[0]?.clientX;
    const startY = e.clientY ?? e.touches?.[0]?.clientY;
    const startFx = draft.cover_focus_x;
    const startFy = draft.cover_focus_y;
    dragRef.current = { box, startX, startY, startFx, startFy };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const cx = ev.clientX ?? ev.touches?.[0]?.clientX;
      const cy = ev.clientY ?? ev.touches?.[0]?.clientY;
      const dx = ((cx - d.startX) / d.box.width) * 100;
      const dy = ((cy - d.startY) / d.box.height) * 100;
      setDraft((s) => ({
        ...s,
        cover_focus_x: Math.max(0, Math.min(100, d.startFx - dx)),
        cover_focus_y: Math.max(0, Math.min(100, d.startFy - dy)),
      }));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
  };

  const view = editMode && draft ? { ...n, ...draft } : n;
  // Compute inline styles for the cover image. In edit mode we use the
  // draft state; otherwise the persisted fields. `cover_scale_x/y` (new,
  // non-uniform) override the legacy uniform `cover_scale` when present.
  const buildTransform = (fx, fy, sx, sy) => ({
    objectPosition: `${fx}% ${fy}%`,
    transform: `scale(${sx}, ${sy})`,
    transformOrigin: `${fx}% ${fy}%`,
  });
  const coverStyle = editMode
    ? buildTransform(
        draft.cover_focus_x,
        draft.cover_focus_y,
        draft.cover_scale_x ?? draft.cover_scale ?? 1,
        draft.cover_scale_y ?? draft.cover_scale ?? 1,
      )
    : n && (
        typeof n.cover_focus_x === "number" ||
        typeof n.cover_scale_x === "number" ||
        typeof n.cover_scale_y === "number" ||
        n.cover_scale
      )
      ? buildTransform(
          n.cover_focus_x ?? 50,
          n.cover_focus_y ?? 50,
          n.cover_scale_x ?? n.cover_scale ?? 1,
          n.cover_scale_y ?? n.cover_scale ?? 1,
        )
      : undefined;

  return (
    <article data-testid="news-detail" className="max-w-3xl mx-auto px-4 py-6 relative">
      {canEdit && (
        <div
          className="sticky top-2 z-40 flex justify-end mb-2"
          data-testid="news-edit-controls"
        >
          <div className="bg-white shadow-lg border border-gray-200 rounded-full px-3 py-1.5 flex items-center gap-3 text-xs">
            <span className="font-semibold text-gray-600">Edit Modu</span>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={editMode}
                onChange={(e) => {
                  if (e.target.checked) enterEdit();
                  else saveEdit();
                }}
                data-testid="news-edit-toggle"
              />
              <span className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-[#e11d2a] transition-colors relative before:content-[''] before:absolute before:w-4 before:h-4 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 peer-checked:before:translate-x-4 before:transition-transform" />
            </label>
            {editMode && (
              <>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  data-testid="news-edit-save"
                  className="text-emerald-600 hover:underline inline-flex items-center gap-1"
                >
                  <Save className="w-3 h-3" /> {saving ? "..." : "Kaydet"}
                </button>
                <button
                  onClick={cancelEdit}
                  data-testid="news-edit-cancel"
                  className="text-gray-500 hover:underline"
                >
                  İptal
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-2">
        {n.category && <span className="px-2 py-0.5 text-xs font-bold uppercase rounded-sm text-white" style={{ background: n.category.color || "#e11d2a" }}>{n.category.name}</span>}
        {n.province && <Link to="/" className="text-xs text-gray-500">{n.province.name}</Link>}
      </div>

      {editMode ? (
        <>
          <input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            className="w-full text-3xl md:text-4xl font-black leading-tight bg-yellow-50 border border-dashed border-amber-300 rounded px-2 py-1 outline-none"
            data-testid="news-edit-title"
          />
          <textarea
            value={draft.spot}
            onChange={(e) => setDraft({ ...draft, spot: e.target.value })}
            rows={2}
            className="w-full text-lg text-gray-700 mt-3 bg-yellow-50 border border-dashed border-amber-300 rounded px-2 py-1 outline-none"
            data-testid="news-edit-spot"
          />
        </>
      ) : (
        <>
          <h1 className="text-3xl md:text-4xl font-black leading-tight">{view.title}</h1>
          <p className="text-lg text-gray-600 mt-3">{view.spot}</p>
        </>
      )}

      <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(n.publish_at).toLocaleString("tr-TR")}</span>
        <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {n.views || 0}</span>
        <button onClick={save} className="flex items-center gap-1 hover:text-[#e11d2a]"><Bookmark className="w-3.5 h-3.5" /> Kaydet</button>
      </div>

      {(view.cover_image || editMode) && (
        <div
          className={`relative w-full aspect-[16/9] rounded-lg mt-5 overflow-hidden bg-gray-100 ${editMode ? "cursor-move ring-2 ring-amber-400" : ""}`}
          data-testid="news-cover-wrapper"
          onMouseDown={editMode ? onCoverDown : undefined}
          onTouchStart={editMode ? onCoverDown : undefined}
        >
          {view.cover_image ? (
            <img
              src={view.cover_image}
              alt={view.title}
              className="w-full h-full object-cover select-none"
              draggable={false}
              style={coverStyle}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Kapak yok</div>
          )}
          {editMode && (
            <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white p-2 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setShowMediaPicker((s) => !s)}
                data-testid="news-edit-change-photo"
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-white/20 hover:bg-white/30"
              >
                <ImagePlus className="w-3 h-3" /> Fotoğraf Değiştir
              </button>
              <span className="text-[11px] inline-flex items-center gap-1"><Move className="w-3 h-3" /> Sürükleyerek konumlandır</span>
              <label className="inline-flex items-center gap-2 text-[11px]">
                <ZoomIn className="w-3 h-3" /> X
                <input
                  type="range" min="0.5" max="3" step="0.05"
                  value={draft.cover_scale_x ?? 1}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setDraft({
                      ...draft,
                      cover_scale_x: v,
                      cover_scale_y: draft.cover_link_axes ? v : (draft.cover_scale_y ?? 1),
                      cover_scale: draft.cover_link_axes ? v : draft.cover_scale,
                    });
                  }}
                  className="w-24 accent-[#e11d2a]"
                  data-testid="news-edit-scale-x"
                />
                <span className="tabular-nums w-9">{Number(draft.cover_scale_x ?? 1).toFixed(2)}x</span>
              </label>
              <label className="inline-flex items-center gap-2 text-[11px]">
                <ZoomIn className="w-3 h-3 rotate-90" /> Y
                <input
                  type="range" min="0.5" max="3" step="0.05"
                  value={draft.cover_scale_y ?? 1}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setDraft({
                      ...draft,
                      cover_scale_y: v,
                      cover_scale_x: draft.cover_link_axes ? v : (draft.cover_scale_x ?? 1),
                      cover_scale: draft.cover_link_axes ? v : draft.cover_scale,
                    });
                  }}
                  className="w-24 accent-[#e11d2a]"
                  data-testid="news-edit-scale-y"
                />
                <span className="tabular-nums w-9">{Number(draft.cover_scale_y ?? 1).toFixed(2)}x</span>
              </label>
              <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!draft.cover_link_axes}
                  onChange={(e) => setDraft({ ...draft, cover_link_axes: e.target.checked })}
                  className="w-3 h-3 accent-[#e11d2a]"
                  data-testid="news-edit-link-axes"
                />
                Eksenleri Bağla
              </label>
              <button
                type="button"
                onClick={() => setDraft({
                  ...draft,
                  cover_focus_x: 50, cover_focus_y: 50,
                  cover_scale: 1, cover_scale_x: 1, cover_scale_y: 1,
                })}
                className="text-[11px] underline"
              >
                Sıfırla
              </button>
            </div>
          )}
          {editMode && showMediaPicker && (
            <div className="absolute inset-0 bg-white/95 p-4 overflow-auto z-10" data-testid="news-edit-media-picker">
              <div className="max-w-md mx-auto">
                <MediaPicker
                  value={draft.cover_image}
                  onChange={(u) => { setDraft({ ...draft, cover_image: u }); setShowMediaPicker(false); }}
                  scope="news"
                  testId="news-edit-cover-picker"
                />
                <button
                  type="button"
                  onClick={() => setShowMediaPicker(false)}
                  className="mt-3 text-xs text-gray-500 hover:underline"
                >
                  Kapat
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {editMode ? (
        <textarea
          value={draft.content}
          onChange={(e) => setDraft({ ...draft, content: e.target.value })}
          rows={14}
          className="w-full mt-6 text-gray-800 text-base leading-7 bg-yellow-50 border border-dashed border-amber-300 rounded px-3 py-2 outline-none font-mono"
          placeholder="Haber metni... (HTML destekli)"
          data-testid="news-edit-content"
        />
      ) : (
        <RichContent
          text={view.content}
          className="prose prose-lg max-w-none mt-6 text-gray-800 space-y-4"
        />
      )}

      {/* Admin-only "Kaynak" block.
       * Rendered only when the current user has edit rights so public
       * visitors never see internal attribution. In edit mode admins can
       * update the text and the optional URL inline. */}
      {canEdit && (editMode || n.source || n.source_url) && (
        <div
          className="mt-6 border border-dashed border-amber-300 bg-amber-50/40 rounded-md px-3 py-2"
          data-testid="news-admin-source"
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">
            Kaynak (sadece admin görür)
          </div>
          {editMode ? (
            <div className="grid md:grid-cols-2 gap-2">
              <input
                type="text"
                value={draft.source ?? ""}
                onChange={(e) => setDraft({ ...draft, source: e.target.value })}
                placeholder="Kaynak (metin) — AA / DHA / Muhabirimiz…"
                className="w-full px-2 py-1.5 text-sm border border-amber-200 rounded bg-white"
                data-testid="news-edit-source"
              />
              <input
                type="url"
                value={draft.source_url ?? ""}
                onChange={(e) => setDraft({ ...draft, source_url: e.target.value })}
                placeholder="Kaynak URL (opsiyonel) — https://…"
                className="w-full px-2 py-1.5 text-sm border border-amber-200 rounded bg-white"
                data-testid="news-edit-source-url"
              />
            </div>
          ) : (
            <div className="text-sm text-gray-800 flex flex-wrap items-center gap-2">
              {n.source && <span className="font-medium">{n.source}</span>}
              {n.source_url && (
                <a
                  href={n.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#e11d2a] hover:underline break-all"
                >
                  {n.source_url}
                </a>
              )}
              {!n.source && !n.source_url && (
                <span className="text-gray-400 italic text-xs">— boş —</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Admin-only schedule controls — same visibility rules as the source
       * block. publish_at defines when the item becomes visible to the
       * public, expire_at silently hides it afterwards (used for events,
       * giveaways, time-limited breaking news, etc.). */}
      {canEdit && editMode && (
        <div
          className="mt-3 border border-dashed border-amber-300 bg-amber-50/40 rounded-md px-3 py-2"
          data-testid="news-admin-schedule"
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">
            Yayın takvimi (sadece admin görür)
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <label className="block text-xs text-gray-700">
              <span className="block mb-0.5">Yayın tarihi/saati</span>
              <input
                type="datetime-local"
                value={draft.publish_at ?? ""}
                onChange={(e) => setDraft({ ...draft, publish_at: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-amber-200 rounded bg-white"
                data-testid="news-edit-publish-at"
              />
            </label>
            <label className="block text-xs text-gray-700">
              <span className="block mb-0.5 flex items-center justify-between">
                <span>Yayından kaldırma tarihi/saati</span>
                {draft.expire_at && (
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, expire_at: "" })}
                    className="text-[10px] text-amber-700 underline"
                    data-testid="news-edit-expire-clear"
                  >
                    temizle
                  </button>
                )}
              </span>
              <input
                type="datetime-local"
                value={draft.expire_at ?? ""}
                onChange={(e) => setDraft({ ...draft, expire_at: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-amber-200 rounded bg-white"
                data-testid="news-edit-expire-at"
              />
            </label>
          </div>
          <p className="mt-1 text-[10px] text-gray-500">
            Yayından kaldırma zamanı geldiğinde haber otomatik olarak önyüzden gizlenir;
            istediğinizde tekrar yayına almak için bu alanı temizleyebilirsiniz.
          </p>
        </div>
      )}

      {n.gallery?.length > 0 && (
        <section className="mt-8" data-testid="news-gallery">
          <h3 className="text-lg font-black mb-3">Galeri</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {n.gallery.map((url, i) => (
              <a
                key={`${url}-${i}`}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="block aspect-square overflow-hidden rounded-md bg-gray-100 group"
                data-testid={`news-gallery-item-${i}`}
              >
                <img
                  src={url}
                  alt={`${n.title} - ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        </section>
      )}

      <div className="flex items-center gap-2 mt-6 border-t border-b py-4">
        <span className="text-sm font-semibold flex items-center gap-1"><Share2 className="w-4 h-4" /> Paylaş:</span>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" aria-label="Facebook'ta paylaş" data-testid="share-facebook" className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"><Facebook className="w-4 h-4" /></a>
        <a href={`https://twitter.com/intent/tweet?text=${shareTitle}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" aria-label="X'te (Twitter) paylaş" data-testid="share-twitter" className="p-2 bg-black hover:bg-gray-800 text-white rounded-full transition-colors"><Twitter className="w-4 h-4" /></a>
        <a href={`https://wa.me/?text=${shareTitle} ${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" aria-label="WhatsApp'ta paylaş" data-testid="share-whatsapp" className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"><Send className="w-4 h-4" /></a>
        <a href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${shareTitle}`} target="_blank" rel="noreferrer" aria-label="Telegram'da paylaş" data-testid="share-telegram" className="p-2 bg-sky-500 hover:bg-sky-600 text-white rounded-full transition-colors"><Send className="w-4 h-4" /></a>
        <StoryPreviewButton
          url={storyUrl}
          filename={`sine81-${n.slug}.png`}
          title={n.title}
          text={`${n.title} — SİNE81`}
          testIdShare="share-story"
          testIdPreview="share-story-preview"
        />
        <button
          type="button"
          onClick={shareInstagram}
          aria-label="Instagram'da paylaş"
          data-testid="share-instagram"
          className="p-2 text-white rounded-full transition-opacity hover:opacity-90"
          style={{
            background:
              "radial-gradient(circle at 30% 110%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
          }}
        >
          <Instagram className="w-4 h-4" />
        </button>
      </div>

      {related?.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xl font-black mb-3">İlgili Haberler</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {related.slice(0, 3).map((r) => (
              <Link key={r.id} to={`/haber/${r.slug}`} className="block group">
                {r.cover_image && <img src={r.cover_image} alt={r.title} className="aspect-[16/10] w-full object-cover rounded-md" />}
                <div className="text-sm font-semibold mt-2 line-clamp-2 group-hover:text-[#e11d2a]">{r.title}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
